import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { signOutHandler } from '@utils/helpers/lib/login';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { useUserDetailsByUserIdQuery } from '@ports/internal-services/userDetails/userDetailsSlice';
import { useEffect, useState } from 'react';
import { getFirstLogin, setPermissions } from '@ports/common/authStateSlice';
import { useMeQuery } from '@ports/internal-services/user/userSlice';
import { setSubscriberId } from '@ports/common/sessionStateSlice';
import { setUserData } from '@ports/common/userStateSlice';
import { useGetMySpaceQuery } from '@ports/internal-services/invite/inviteSlice';
import { Avatar } from '../Avatar';
import { Button } from '../Button';

export const Header = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isFirstLogin = useSelector(getFirstLogin);
  const { data: userDetails, refetch } = useUserDetailsByUserIdQuery({} as any);
  const { data: mySpaceData, refetch: refetchMySpace } = useGetMySpaceQuery();
  const mySpaceProfiles = mySpaceData?.data ?? [];

  const [selectedUserId, setSelectedUserId] = useState<
    string | null | undefined
  >(undefined);

  useEffect(() => {
    refetchMySpace();
  }, []);

  const handleSwitchProfile = (subscriptionId?: string, userId?: string) => {
    if (!subscriptionId || userId === selectedUserId) return;
    setSelectedUserId(userId);
    dispatch(setSubscriberId({ subscriberId: subscriptionId }));
  };

  useEffect(() => {
    refetch();
  }, []);

  const { data: loggedUserData, refetch: refetchMe } = useMeQuery(
    undefined,
    {}
  );

  useEffect(() => {
    refetchMe();
  }, []);

  useEffect(() => {
    if (loggedUserData?.data?.subscriberId) {
      dispatch(
        setSubscriberId({
          subscriberId: loggedUserData?.data?.subscriberId
        })
      );
    }
    if (loggedUserData?.data?.permissions?.length > 0) {
      dispatch(
        setPermissions({
          permissions: loggedUserData?.data?.permissions || []
        })
      );
    }
    dispatch(
      setUserData({
        fullName: loggedUserData?.data?.fullName,
        email: loggedUserData?.data?.email,
        userId: loggedUserData?.data?.userId,
        profileImageUrl: userDetails?.data?.profileImage
      })
    );

    if (loggedUserData?.data?.userId && !selectedUserId) {
      setSelectedUserId(loggedUserData?.data?.userId);
    }
  }, [loggedUserData, userDetails]);

  const fullName =
    userDetails?.data?.firstName ?? loggedUserData?.data?.fullName;

  const dropdownItems = [
    {
      label: t('logout'),
      onClick: () => signOutHandler(dispatch, navigate),
      decorator: <SVGAssets.LogoutIcon className="w-5 h-5 mr-2" />
    }
  ];

  return (
    <header className="flex-shrink-0 bg-white shadow-sm z-50">
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <SVGAssets.HeaderLogo className="h-12" />
          </div>
          <div className="relative group">
            <div className="flex items-center cursor-pointer">
              <Avatar
                alt={`${fullName}`}
                className="w-10 h-10 overflow-hidden rounded-full"
                src={userDetails?.data?.profileImage}
                isHeader={true}
              />

              <SVGAssets.PricingArrow className="w-2 -rotate-90 ml-2" />
            </div>

            <ul
              className="hidden group-hover:block absolute right-0 z-50 shadow-lg bg-white rounded-lg w-82 py-2 border border-gray-100 hover:block"
              id="sideNav">
              <li className="flex items-center gap-3 p-3">
                <Avatar
                  alt={`${fullName}`}
                  className="w-20 h-20 overflow-hidden rounded-full"
                  src={userDetails?.data?.profileImage}
                  isHeader={true}
                />
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <span className="text-[15px] font-semibold text-[#3F602B]">
                      {fullName}
                    </span>
                    {!isFirstLogin && (
                      <Button
                        btnStyle="link"
                        href={routePaths.EditProfile}
                        className="p-0">
                        <SVGAssets.ProfileEditIcon className="w-4 h-4 text-[#3F602B]" />
                      </Button>
                    )}
                  </div>
                  <span className="text-xs text-[#8D8D8D] w-50 w-full">
                    {loggedUserData?.data?.email}
                  </span>
                  {!isFirstLogin && (
                    <Button
                      btnStyle="link"
                      href={routePaths.MyProfile}
                      className="text-[13px] text-[#6B6B6B] mt-1 hover:bg-[#ebe5e5] px-1 py-2 rounded-md">
                      {t('accountSettings')}
                    </Button>
                  )}
                </div>
              </li>

              {mySpaceProfiles.length > 0 && (
                <>
                  <li className="bg-[#F0F7E4] px-4 py-2">
                    <span className="text-[#3F602B] font-semibold text-sm">
                      {t('switchProfile')}
                    </span>
                  </li>
                  {mySpaceProfiles.map(profile => {
                    const isActive = profile.userId === selectedUserId;
                    const isOwner =
                      profile.userId === loggedUserData?.data?.userId;
                    const displayName = isOwner
                      ? `You(${profile.name})`
                      : profile.name;
                    return (
                      <li
                        key={profile.subscriberId ?? profile.userId}
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                        onClick={() =>
                          handleSwitchProfile(
                            profile.subscriberId,
                            profile.userId
                          )
                        }>
                        <Avatar
                          alt={`${profile.name}`}
                          className="w-10 h-10 overflow-hidden rounded-full flex-shrink-0"
                          src={profile.profileImage}
                          isHeader={false}
                        />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm font-semibold text-gray-800 truncate">
                            {displayName}
                          </span>
                          <span className="text-xs text-gray-500 truncate">
                            {profile.roleName}
                          </span>
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          {isActive ? (
                            <div className="w-6 h-6 rounded-full bg-[#3F602B] flex items-center justify-center">
                              <svg
                                className="w-3.5 h-3.5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}>
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </>
              )}

              {dropdownItems.map((item, index) => (
                <li
                  key={index}
                  className="w-full flex items-center bg-[#F9FEE9] px-4 py-2 hover:bg-[#ebe5e5]">
                  <span>{item?.decorator}</span>
                  <Button
                    btnStyle="link"
                    className="text-[#49712B]font-normal text-sm py-2 px-4 w-full text-left"
                    onClick={item.onClick}>
                    {item?.label}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};
